import React from 'react';
import { Form, Button, Card, Row, Col, Spinner } from 'react-bootstrap';

import FormField from './FormField';

const GenericForm = ({ title, onSubmit, form, handleChange, formErrors, submitting, editId, onCancel, fields }) => {
  let sections = fields || [];
  if (sections.length > 0 && !sections[0].fields && sections[0].name) {
    sections = [{
      title: `${title} Details`,
      fields: fields
    }];
  }

  const handleCheckboxChange = (fieldName, value) => {
    const currentValues = Array.isArray(form[fieldName]) ? form[fieldName] : [];
    const valueStr = value.toString();
    
    let newValues;
    if (currentValues.includes(valueStr)) {
      newValues = currentValues.filter(v => v !== valueStr);
    } else {
      newValues = [...currentValues, valueStr];
    }
    
    handleChange({ target: { name: fieldName, value: newValues } });
  };

  return (
    <Card className="mb-4 shadow-sm">
      <Card.Header as="h5" className="bg-light-subtle">
        {editId ? `Edit ${title}` : `Add New ${title}`}
      </Card.Header>
      <Card.Body>
        <Form noValidate onSubmit={onSubmit}>
          {sections.map((section, sectionIndex) => (
            <div key={section.title || sectionIndex} className={sectionIndex > 0 ? 'mt-4 pt-4 border-top' : ''}>
              {section.title && <h6 className="mb-3 text-primary">{section.title}</h6>}
              <Row className="g-3">
                {(section.fields || []).map((field) => {
                  if (editId && field.hideOnEdit) {
                    return null;
                  }

                  if (field.as === 'select' && field.multiple && field.renderAsCheckboxes) {
                    const Icon = field.icon;
                    const fieldValue = form[field.name] || [];

                    return (
                      <Col md={field.colSize || 12} key={field.name}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {Icon && <Icon className="me-2" />}
                            {field.label}
                            {field.required && <span className="text-danger ms-1">*</span>}
                          </Form.Label>
                          <div 
                            className="border rounded p-3 bg-white" 
                            style={{ maxHeight: '250px', overflowY: 'auto' }}
                          >
                            {field.options && field.options.length > 0 ? (
                              <div className="d-flex flex-column gap-2">
                                {field.options.map((option) => {
                                  const optionValue = option.value.toString();
                                  const isChecked = Array.isArray(fieldValue) && fieldValue.includes(optionValue);
                                  
                                  return (
                                    <Form.Check
                                      key={option.value}
                                      type="checkbox"
                                      id={`${field.name}-${option.value}`}
                                      label={option.label}
                                      checked={isChecked}
                                      onChange={() => handleCheckboxChange(field.name, option.value)}
                                      className="user-select-none"
                                      disabled={field.disabled || (editId && field.disableOnEdit)}
                                    />
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-muted mb-0 text-center py-3">No options available</p>
                            )}
                          </div>
                          {field.helpText && (
                            <Form.Text className="text-muted d-block mt-2">
                              {field.helpText}
                            </Form.Text>
                          )}
                          {formErrors[field.name] && (
                            <div className="text-danger small mt-1">{formErrors[field.name]}</div>
                          )}
                        </Form.Group>
                      </Col>
                    );
                  }

                  const colSize = field.colSize || (field.as === 'textarea' || field.fullWidth ? 12 : 6);

                  return (
                    <Col md={colSize} key={field.name}>
                      <FormField
                        {...field}
                        value={form[field.name]}
                        onChange={handleChange}
                        error={formErrors[field.name]}
                        disabled={field.disabled || (editId && field.disableOnEdit)}
                      />
                    </Col>
                  );
                })}
              </Row>
            </div>
          ))}

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="secondary" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                  {editId ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                editId ? 'Update' : 'Add'
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default GenericForm;